import React from "react"
import { Container, Row, Col, Table, Spinner, Button, Dropdown } from "react-bootstrap"
import { BehaviorSubject, Subscription, concat, of } from "rxjs"
import { map, switchMap } from "rxjs/operators"
import { toast } from "react-toastify"
import { FaPrint } from "react-icons/fa"
import FileSaver from "file-saver"
import PoolMemberRecord from "../../../api/Randoms/PoolMembersModel/PoolMemberRecord"
import RandomPersonnelIdentifier from "../../../api/Randoms/RandomPersonnelModel/RandomPersonnelIdentifier"
import { PoolMembersGridObservable, PoolMembersExportCsv, PoolMembersPrint, ChangePersonStatus, PoolMembersOptions, ColumnFilters, PoolMembersGridResponse } from "../../../api/Randoms/PoolMembersGridGet"
import AutoFilterCompany from "../../../api/Companies/CompanyModel/AutoFilterCompany"
import AutoFilterLocation from "../../../api/Location/LocationModel/AutoFilterLocation"
import AutoFilterRegion from "../../../api/Regions/Model/AutoFilterRegion"
import RegulatoryMode from "../../../api/LabService/LabServiceModel/RegulatoryMode"
import EmployeeCategory from "../../../api/LabService/LabServiceModel/EmployeeCategory"
import RandomPersonnelCheckbox from "../RandomPersonnelCheckbox"
import ColumnsCustomizeModal from "../../ColumnsCustomizeModal/ColumnsCustomizeModal"
import EmployeeDetailsModal from "../../OrderResults/EmployeeDetailsModal"
import TablePagination from "../../TablePagination"
import ColumnHeaderFilter from "../../ColumnHeaderFilter"
import SearchField from "../../../api/SearchField"
import PoolMembersFiltersModal from "../PoolMembersFiltersModal"
import PoolMembersCriteriaModal from "../PoolMembersCriteriaModal"
import AddPoolMemberModal from "../AddPoolMemberModal"
import FormatSSN from "../../../formatters/FormatSSN"
import FormatDate from "../../../formatters/FormatDate"
import FormatTimestampFileName from "../../../formatters/FormatTimestampFileName"
import ActionButton from "../../ActionButton"
import Checkbox from "../../Checkbox"
import { CustomizableFilterColumn } from "../../ColumnsCustomizeModal/CustomizableColumn"
import { AppState, CurrentUserPermissions } from "../../../AppState"
import { connect } from "react-redux"
import { StorageKey } from "../../../config/Constants"
import { loadCustomized, saveCustomized } from "../../ColumnsCustomizeModal/ColumnsCustomizeModal"
import { BuildUserAccess } from "../../../authentication/BuildUserAccess"
import  UserAccessSettings from "../../../authentication/UserAccessSettings"
import GetDonorID, { GetDonorIDTitle } from "../../../formatters/RenderDonorID"

type Column = CustomizableFilterColumn<keyof PoolMemberRecord, keyof ColumnFilters>

export enum RandomPersonnelGridMode {
  PersonnelGrid = "PersonnelGrid",
  PoolMembers = "PoolMembers"
}

enum StatusOptions {
  All = "All",
  Active = "Active",
  Inactive = "Inactive"
}

interface PoolMembersGridProps {
  poolID: number
  poolName: string
  useFileImportForPoolMembership: boolean
  regulated: boolean
  userPermissions?: UserAccessSettings
  isConsortium: boolean
  isCompanyAdmin?: boolean
  unmaskDonorId: number
  onPoolUpdate: () => void
}

interface RandomPersonnelGridStateSelectedPersons {
  selectedPrintRowIDs: RandomPersonnelIdentifier[]
  selectedChangeStatusRowIDs: RandomPersonnelIdentifier[]
}

interface PoolMembersGridState extends RandomPersonnelGridStateSelectedPersons {
  loading: boolean
  loadingCsv: boolean
  importing: boolean
  removing: boolean
  data?: PoolMembersGridResponse
  columns: Column[]
  customizeModal: boolean
  advancedFiltersModal: boolean
  addPoolMember: boolean
  searchFields?: SearchField[]
  selectedCompanies: AutoFilterCompany[]
  selectedRegions: AutoFilterRegion[]
  selectedLocations: AutoFilterLocation[]
  selectedRegulatoryModes: RegulatoryMode[]
  selectedCategories: EmployeeCategory[]
  status: StatusOptions
  accountType: string
  employeeDetails: boolean
  details?: PoolMemberRecord
  showPoolMembersCriteriaModal: boolean
}

const defaultColumns: Column[] = [
  { name: "PrimaryID", filter: "IDNumber", label: "PERSONNEL ID", visible: true, customize: false },
  { name: "Name", filter: "Name", label: "NAME", visible: true, customize: true },
  { name: "Company", filter: "Company", label: "COMPANY", visible: true, customize: true },
  { name: "Occupation", filter: "Occupation", label: "OCCUPATION", visible: true, customize: true },
  { name: "Location", filter: "Location", label: "LOCATION", visible: true, customize: true },
  { name: "RegMode", filter: "RegulatoryMode", label: "REGULATORY MODE", visible: true, customize: true },
  { name: "DateActive", filter: "ActiveSince", label: "ACTIVE SINCE", visible: true, customize: true },
  { name: "ReasonForInactivation", filter: "ReasonForInactivation", label: "REASON FOR INACTIVATION", visible: false, customize: true },
  { name: "Category", filter: "Category", label: "EMPLOYEE CATEGORY", visible: false, customize: true },
  { name: "Supervisor1", filter: "Supervisor1", label: "SUPERVISOR 1", visible: false, customize: true },
  { name: "Supervisor2", filter: "Supervisor2", label: "SUPERVISOR 2", visible: false, customize: true },
  { name: "HireDate", filter: "HireDate", label: "HIRE DATE", visible: false, customize: true },
  { name: "TerminationDate", filter: "TerminationDate", label: "TERMINATION DATE", visible: false, customize: true },
  { name: "PreEmploymentTestOnFile", filter: "PreEmploymentTestOnFile", label: "PRE-EMPLOYMENT TEST ON FILE", visible: false, customize: true },
  { name: "Gender", filter: "Gender", label: "GENDER", visible: false, customize: true },
  { name: "DateOfBirth", filter: "DateOfBirth", label: "DATE OF BIRTH", visible: false, customize: true },
  { name: "AddressLine1", filter: "AddressLine1", label: "STREET ADDRESS", visible: false, customize: true },
  { name: "City", filter: "City", label: "CITY", visible: false, customize: true },
  { name: "State", filter: "State", label: "STATE", visible: false, customize: true },
  { name: "Zip", filter: "Zip", label: "ZIP", visible: false, customize: true },
  { name: "WorkPhone", filter: "WorkPhone", label: "WORK PHONE", visible: false, customize: true },
  { name: "CellPhone", filter: "CellPhone", label: "MOBILE PHONE", visible: false, customize: true },
  { name: "Email", filter: "Email", label: "EMAIL", visible: false, customize: true },
  { name: "User1", filter: "User1", label: "USER 1", visible: false, customize: true },
  { name: "User2", filter: "User2", label: "USER 2", visible: false, customize: true },
  { name: "User3", filter: "User3", label: "USER 3", visible: false, customize: true },
  { name: "User4", filter: "User4", label: "USER 4", visible: false, customize: true },
  { name: "User5", filter: "User5", label: "USER 5", visible: false, customize: true },
  { name: "User6", filter: "User6", label: "USER 6", visible: false, customize: true },
  { name: "User7", filter: "User7", label: "USER 7", visible: false, customize: true },
  { name: "User8", filter: "User8", label: "USER 8", visible: false, customize: true },
  { name: "User9", filter: "User9", label: "USER 9", visible: false, customize: true },
  { name: "User10", filter: "User10", label: "USER 10", visible: false, customize: true },
  { name: "Manager", filter: "Manager", label: "MANAGER", visible: false, customize: true },
]

const PageSize = 20

export class PoolMembersGrid extends React.PureComponent<PoolMembersGridProps, PoolMembersGridState> {
  state: PoolMembersGridState = {
    loading: false,
    loadingCsv: false,
    importing: false,
    removing: false,
    columns: loadCustomized(StorageKey.PoolMembersGridColumns, defaultColumns) as Column[],
    customizeModal: false,
    advancedFiltersModal: false,
    addPoolMember: false,
    selectedPrintRowIDs: [],
    selectedChangeStatusRowIDs: [],
    selectedCompanies: [],
    selectedRegions: [],
    selectedLocations: [],
    selectedRegulatoryModes: [],
    selectedCategories: [],
    status: StatusOptions.Active,
    accountType: "master", // TODO: Get it from Account data once Auth/Account will be implemented
    employeeDetails: false,
    showPoolMembersCriteriaModal: false
  }
  page$ = new BehaviorSubject<PoolMembersOptions>({
    poolId: this.props.poolID,
    inactive: false,
    search: { field: "", text: "" },
    sort: { column: "", order: "" },
    filters: [],
    columnFilters: [],
    pageNumber: 1,
    pageSize: PageSize
  })
  pageSubscription?: Subscription

  file = React.createRef<HTMLInputElement>()

  componentDidMount() {
    this.pageSubscription = this.page$.pipe(
      switchMap(params => concat(
        of({ loading: true }),
        PoolMembersGridObservable(params).pipe(
          map((result: PoolMembersGridResponse) => ({ data: result, loading: false })))
      ))
    ).subscribe(
      (newState: { loading: boolean, data?: PoolMembersGridResponse }) => {
        if (newState.data) {
          if (newState.data.ActiveSince?.length) {
            newState.data.ActiveSince = newState.data.ActiveSince.map(dateString => FormatDate(dateString))
          }
          if (newState.data.HireDate?.length) {
            newState.data.HireDate = newState.data.HireDate.map(dateString => FormatDate(dateString))
          }
          if (newState.data.TerminationDate?.length) {
            newState.data.TerminationDate = newState.data.TerminationDate.map(dateString => FormatDate(dateString))
          }
          if (newState.data.DateOfBirth?.length) {
            newState.data.DateOfBirth = newState.data.DateOfBirth.map(dateString => FormatDate(dateString))
          }
          if (!this.props.userPermissions?.isPortalAdmin && newState.data.Manager?.length) {
            this.setState({columns: this.state.columns.filter( x => x.name != "Manager")})
          }
          this.setState({ loading: newState.loading, data: newState.data, selectedPrintRowIDs: [], selectedChangeStatusRowIDs: [] })
        } else {
          this.setState({ loading: newState.loading })
        }
      },
      (error: string) => {
        this.setState({ loading: false })
        toast.error(error)
      }
    )
  }

  componentWillUnmount() {
    this.pageSubscription && this.pageSubscription.unsubscribe()
  }

  visibleColumns = () => this.state.columns.filter(col => col.visible)

  renderCell = (row: PoolMemberRecord, colName: keyof PoolMemberRecord, key: number) => {
    switch (colName) {
      case "PrimaryID":
        const idValue = GetDonorID(row["DonorID"], row[colName], this.props.unmaskDonorId)
        return <td key={key} className="align-text-top"><Button variant="link" className="p-0" onClick={() => this.showEmployeeDetails(row)}>{idValue}</Button></td>
      case "DateActive":
      case "HireDate":
      case "TerminationDate":
      case "DateOfBirth":
        return <td key={key} className="align-text-top">{FormatDate(row[colName] as string)}</td>
      case "Manager":
        return <td key={key} className="align-text-top">{row[colName].toString().toUpperCase()}</td>
      default:
        return <td key={key} className="align-text-top">{row[colName]}</td>
    }
  }

  sortGrid(column: string, order?: string) {
    this.page$.next({ ...this.page$.value, sort: { column: column, order: order || "" } })
  }

  filterGridColumn(column: string, items: string[]) {
    const columnFilters = this.page$.value.columnFilters
    const filter = { column: column, values: items }
    let index = columnFilters.findIndex(item => item.column === filter.column)
    if (index >= 0) {
      columnFilters[index] = filter
    } else {
      columnFilters.push(filter)
    }

    this.page$.next({ ...this.page$.value, pageNumber: 1, columnFilters: columnFilters })
  }

  handleExportClick(fileName: string) {
    this.setState({ loadingCsv: true })
    PoolMembersExportCsv(this.page$.value).subscribe(
      result => this.export(result, fileName),
      error => {
        toast.error(error)
        this.setState({ loadingCsv: false })
      },
      (() => this.setState({ loadingCsv: false }))
    )
  }

  export(csv: string, fileName: string) {
    const csvData = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    FileSaver.saveAs(csvData, FormatTimestampFileName(fileName, "csv", new Date()), true)
    toast.success("CSV file downloaded successfully.")
  }

  handlePrintClick() {
    PoolMembersPrint(this.state.selectedPrintRowIDs.map(row => row.PersonID), this.page$.value).subscribe(
      (data: string) => this.print(data),
      error => toast.error(error)
    )
  }

  print(base64pdf: string) {
    const pdfWindow = window.open("")
    if (pdfWindow) {
      pdfWindow.document.write("<iframe width='100%' height='100%' src='data:application/pdf;base64, " + encodeURI(base64pdf) + "'></iframe>")
    }
  }

  handleChangePersonsStatusClick() {
    ChangePersonStatus(this.props.poolID, this.state.selectedChangeStatusRowIDs.map(row => row.PersonID), 0, 0).subscribe(
      data => {
        this.setState({ removing: false, selectedChangeStatusRowIDs: [] })
        this.refreshGrid()
        /*data.error
          ? toast.error("Person activation failed.")
          : toast.success(data.message)*/
        toast.success(data)
      },
      error => toast.error(error)
    )
  }

  handlePrintSelectAllRowsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedPrintRowIDs = e.target.checked && this.state.data ? this.state.data.Records.map((row: PoolMemberRecord, i: number) => ({ PersonID: row.PersonID, ID: row.PrimaryID })) : []
    this.setState({ selectedPrintRowIDs: selectedPrintRowIDs })
  }

  changePersonRowSelection = (person: RandomPersonnelIdentifier, checked: boolean, stateFieldName: keyof RandomPersonnelGridStateSelectedPersons) => {
    let selectedRows = [...this.state[stateFieldName]]
    const index = selectedRows.findIndex(item => item.PersonID === person.PersonID && item.ID === person.ID)
    if (index > -1) {
      selectedRows = [...selectedRows.slice(0, index), ...selectedRows.slice(index + 1)]
    } else {
      selectedRows.push(person)
    }
    this.setState({ ...{}, [stateFieldName]: selectedRows })
  }

  handlePrintSelectRowChange = (person: RandomPersonnelIdentifier, checked: boolean) => {
    this.changePersonRowSelection(person, checked, "selectedPrintRowIDs")
  }

  handleChangePersonStatusSelectRowChange = (person: RandomPersonnelIdentifier, checked: boolean) => {
    this.changePersonRowSelection(person, checked, "selectedChangeStatusRowIDs")
  }

  handleChangePage(pageNumber: number) {
    this.page$.next({ ...this.page$.value, pageNumber: pageNumber })
  }

  handleChangeStatusFilter(status: StatusOptions) {
    this.setState({ status: status })
    /*const filters = this.page$.value.filters.filter(filter => filter.column !== "Inactive")
    if (status !== "All") {
      filters.push({ column: "Inactive", values: [status.toString()] })
    }*/
    const inactive = (status === StatusOptions.All) ? null : status === StatusOptions.Inactive
    this.page$.next({ ...this.page$.value, inactive: inactive/*, filters: filters*/ })
  }

  handleSearch = (field: string, text: string) => {
    this.page$.next({ ...this.page$.value, search: { field: field, text: text } })
  }
  handlePoolMembersCriteriaSave = (closeModal: boolean) => {
    this.setState({
      showPoolMembersCriteriaModal: !closeModal
    })
    this.page$.next({ ...this.page$.value, pageNumber: 1})
    this.props.onPoolUpdate()
  }

  handleAdvancedFiltersSave = (companies: AutoFilterCompany[], regions: AutoFilterRegion[], locations: AutoFilterLocation[], regulatoryModes: RegulatoryMode[], categories: EmployeeCategory[], closeModal: boolean) => {
    this.setState({
      selectedCompanies: companies,
      selectedRegions: regions,
      selectedLocations: locations,
      selectedRegulatoryModes: regulatoryModes,
      selectedCategories: categories,
      advancedFiltersModal: !closeModal
    })
    const filters = [
      { column: "Company", values: companies.map(item => item.Name) },
      { column: "Region", values: regions.map(item => item.Name) },
      { column: "Location", values: locations.map(item => item.Name) },
      { column: "Reg", values: regulatoryModes.map(item => item.Name) },
      { column: "Category", values: categories.map(item => item.Name) }
    ]
    this.page$.next({ ...this.page$.value, filters: filters })
  }

  handleAddIndividualSave = () => {
    this.setState({ addPoolMember: false })
    this.refreshGrid()
  }

  formatSearchString = (ID: string, value: string): string => ID === "SSN" ? FormatSSN(value) : value
  showEmployeeDetails = (data: PoolMemberRecord) => this.setState({ employeeDetails: true, details: data })
  refreshGrid = () => this.page$.next(this.page$.value)

  render() {
    const limitedAccess =  this.props.isConsortium && this.props.isCompanyAdmin
    const headerColumns = this.visibleColumns().map((col: Column, i) => {
      const sort = this.page$.value.sort
      const filterValues = this.page$.value.filters.find(filter => filter.column === col.name)?.values || []
      const message = filterValues.length > 0 ?
        (<>
          <div>Some options in this list may be hidden</div>
          <div>because of the advanced filter configuration.</div>
          <div>Click <span className="text-link" onClick={() => this.setState({ advancedFiltersModal: true })}>filters</span> or ADVANCED FILTERS button to configure</div>
        </>) :
        <></>
      const columnFilterValues = this.page$.value.columnFilters.find(filter => filter.column === col.filter)?.values || []
      return (
        <th key={col.name}>
          {this.state.data && col.filter && col.filter in this.state.data && <ColumnHeaderFilter title={col.label || col.name}
            filtered={columnFilterValues.length > 0}
            filteredItems={columnFilterValues}
            sort={sort && sort.column === col.name ? sort.order === "asc" ? "asc" : "desc" : undefined}
            items={this.state.data[col.filter as keyof ColumnFilters] || []}
            message={message}
            onSort={(order?: string) => this.sortGrid(col.name, order)}
            onFilter={(items: string[]) => col.filter && this.filterGridColumn(col.filter, items)}
          />}
          {(!this.state.data || !col.filter) && (col.label || col.name)}
        </th>
      )
    })
    const allPrintChecked = !this.state.loading && this.state.data?.Records?.length === this.state.selectedPrintRowIDs.length

    const { useFileImportForPoolMembership, userPermissions } = this.props
    // TODO: Some permissions are missed in AppState:
    //          *userPermissions.CanUpdatePoolMembership
    // const manageIndividual = userPermissions && (userPermissions.CanManagePools || userPermissions.CanUpdatePoolMembership)
    const manageIndividual = userPermissions?.canManagePools
    return (
      <>
        {this.state.employeeDetails && this.state.details && (
          <EmployeeDetailsModal
            personID={this.state.details.PersonID}
            poolID={this.props.poolID}
            poolName={this.props.poolName}
            name={this.state.details.Name}
            id={this.state.details.PrimaryID}
            onHide={(updated) => this.setState({ employeeDetails: false }, () => updated && this.refreshGrid())}
          />
        )}
        <ColumnsCustomizeModal
          show={this.state.customizeModal}
          columns={this.props.userPermissions?.isPortalAdmin ? this.state.columns : this.state.columns.filter( x => x.name != "Manager")}
          storeKey={StorageKey.PoolMembersGridColumns}
          onClose={() => this.setState({ customizeModal: false })}
          onSave={(columns) => this.setState({ columns: columns as Column[], customizeModal: false })}
        />
        {this.state.showPoolMembersCriteriaModal && (
          <PoolMembersCriteriaModal
            show={this.state.showPoolMembersCriteriaModal}
            poolId={this.props.poolID}
            poolName={this.props.poolName}
            regulated={this.props.regulated}
            isConsortium={this.props.isConsortium}
            useFileImportForPoolMembership={this.props.useFileImportForPoolMembership}
            onClose={() => this.setState({ showPoolMembersCriteriaModal: false })}
            onSave={(close) => this.handlePoolMembersCriteriaSave(close)}
          />
        )}
        <PoolMembersFiltersModal
          show={this.state.advancedFiltersModal}
          selectedLocations={this.state.selectedLocations}
          selectedRegulatoryModes={this.state.selectedRegulatoryModes}
          onClose={() => this.setState({ advancedFiltersModal: false })}
          onSave={(locations, regModes, close) =>
            this.handleAdvancedFiltersSave([], [], locations, regModes, [], close)
          }
        />
        {
          <AddPoolMemberModal
            show={this.state.addPoolMember}
            poolID={this.props.poolID || 0}
            accountType={this.state.accountType}
            onCancel={() => this.setState({ addPoolMember: false })}
            onAdd={this.handleAddIndividualSave}
          />
        }
        <Container fluid className="grid-container">
          <Row className="pb-2">
            <Col className="mr-0 pr-0">
              <Dropdown className="dropdown-menu-secondary">
                <Dropdown.Toggle size="sm" variant="secondary">
                  {this.state.status}
                </Dropdown.Toggle>
                <Dropdown.Menu className="secondary">
                  <Dropdown.Item
                    as={Button}
                    onClick={() => this.handleChangeStatusFilter(StatusOptions.All)}
                    variant="menu-item"
                  >
                    All
                  </Dropdown.Item>
                  <Dropdown.Item
                    as={Button}
                    onClick={() => this.handleChangeStatusFilter(StatusOptions.Active)}
                    variant="menu-item"
                  >
                    Active
                  </Dropdown.Item>
                  <Dropdown.Item
                    as={Button}
                    onClick={() => this.handleChangeStatusFilter(StatusOptions.Inactive)}
                    variant="menu-item"
                  >
                    Inactive
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
            <Col>
              {(!useFileImportForPoolMembership || this.props.isConsortium) && !limitedAccess && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="mr-1"
                  onClick={() => this.setState({ showPoolMembersCriteriaModal: true })}
                >
                  Criteria
                </Button>
              )}
            </Col>
            <Col lg={7} className="text-right">
              <Button
                variant="secondary"
                size="sm"
                className="mr-1"
                onClick={() => this.setState({ advancedFiltersModal: true })}
              >
                ADVANCED FILTERS
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="mr-1"
                onClick={() => this.setState({ customizeModal: true })}
              >
                CUSTOMIZE
              </Button>
              <ActionButton
                variant="secondary"
                size="sm"
                className="mr-1"
                onClick={() => this.handleExportClick("PoolMembers")}
                disabled={!this.state.data?.Records?.length}
                spin={this.state.loadingCsv}
                disableOnSpin
              >
                EXPORT CSV
              </ActionButton>
              {manageIndividual && (
                <Button variant="secondary" size="sm" onClick={() => this.setState({ addPoolMember: true })}>
                  ADD INDIVIDUAL
                </Button>
              )}
            </Col>
          </Row>
          <Row>
            <Col>
              <Table bordered responsive>
                <thead>
                  <tr>
                    <th className="text-center print-column">
                      <Checkbox
                        id="print_all_members"
                        checked={allPrintChecked}
                        onChange={this.handlePrintSelectAllRowsChange}
                      />
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => this.handlePrintClick()}
                        className="float-right"
                        disabled={this.state.selectedPrintRowIDs.length === 0}
                      >
                        <FaPrint />
                      </Button>
                    </th>
                    {headerColumns}
                    {manageIndividual && (
                      <th className="text-center small-column">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => this.handleChangePersonsStatusClick()}
                          disabled={this.state.selectedChangeStatusRowIDs.length === 0}
                        >
                          {this.state.status === StatusOptions.All && "ACTIVATE/INACTIVATE"}
                          {this.state.status === StatusOptions.Active && "INACTIVATE"}
                          {this.state.status === StatusOptions.Inactive && "ACTIVATE"}
                        </Button>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {this.state.loading && (
                    <tr>
                      <td colSpan={this.visibleColumns().length + 2}>
                        <Spinner animation="border" variant="secondary" />
                      </td>
                    </tr>
                  )}
                  {!this.state.loading &&
                    (this.state.data && this.state.data.Records && this.state.data.Records.length > 0 ? (
                      this.state.data.Records.map((row: PoolMemberRecord, i: number) => {
                        const person = { PersonID: row.PersonID, ID: row.PrimaryID }
                        return (
                          <tr key={`${person.PersonID}_${person.ID}_1_${i}`}>
                            <td className="text-center">
                              <RandomPersonnelCheckbox
                                id={`${person.PersonID}_${person.ID}_check1_${i}`}
                                person={person}
                                checked={
                                  !!this.state.selectedPrintRowIDs.find(
                                    (item) => person.PersonID === item.PersonID && person.ID === item.ID
                                  )
                                }
                                onChange={this.handlePrintSelectRowChange}
                              />
                            </td>
                            {this.visibleColumns().map((col, i) =>
                              this.renderCell(row, col.name as keyof PoolMemberRecord, i)
                            )}
                            {manageIndividual && (
                              <td className="text-center">
                                <RandomPersonnelCheckbox
                                  id={`${person.PersonID}_${person.ID}_check3_${i}`}
                                  person={person}
                                  checked={
                                    !!this.state.selectedChangeStatusRowIDs.find(
                                      (item) => person.PersonID === item.PersonID && person.ID === item.ID
                                    )
                                  }
                                  onChange={this.handleChangePersonStatusSelectRowChange}
                                />
                              </td>
                            )}
                          </tr>
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan={this.visibleColumns().length + 2}>NO DATA FOUND</td>
                      </tr>
                    ))}
                </tbody>
              </Table>
              {this.state.data && !this.state.loading && (
                <span className="total-records">Total records: {this.state.data.Total}</span>
              )}
              {this.state.data && (
                <TablePagination
                  pageNumber={this.page$.value.pageNumber}
                  pages={this.state.data.Pages}
                  onChange={(pageNumber) => this.handleChangePage(pageNumber)}
                />
              )}
            </Col>
          </Row>
        </Container>
      </>
    )
  }
}

function mapStateToProps(state: AppState): { userPermissions: UserAccessSettings } {
  const userAccess = BuildUserAccess(state);
  return {
    userPermissions: userAccess
  }
}
const ConnectedPoolMembersGrid = connect(mapStateToProps, null)(PoolMembersGrid)
export default ConnectedPoolMembersGrid
